import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function RefundPolicyPage() {
  return (
    <div className="min-h-screen bg-[#FCFAF8] flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <span className="inline-block bg-[#FFF5F5] text-[#D48585] text-xs font-body font-semibold px-3 py-1 rounded-full mb-4">Legal</span>
        <h1 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] mb-2" data-testid="refund-policy-title">Refund Policy</h1>
        <p className="font-body text-sm text-[#A89F91] mb-8">Last updated: April 2026</p>

        <div className="prose font-body text-[#4A4745] text-sm leading-relaxed space-y-6">
          <section>
            <h2 className="font-heading text-lg text-[#2C2A29]">1. Overview</h2>
            <p>
              {'{'}Beauty{'}'} Stashmart is a consumer-to-consumer (C2C) marketplace. All products are sold directly by individual sellers,
              not by {'{'}Beauty{'}'} Stashmart. This Refund Policy outlines the conditions under which a buyer may request a refund
              and the process for resolving disputes.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-[#2C2A29]">2. Eligibility for Refund</h2>
            <p>A buyer may request a refund in the following situations:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Item not received:</strong> The seller failed to ship the item within 7 days after payment was confirmed, or the item was lost in transit.</li>
              <li><strong>Item significantly not as described:</strong> The product received is materially different from the listing description (e.g., wrong product, counterfeit, undisclosed major damage, or expired product).</li>
              <li><strong>Damaged during shipping:</strong> The item arrived damaged due to inadequate packaging by the seller.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg text-[#2C2A29]">3. Non-Refundable Situations</h2>
            <p>Refunds will generally <strong>not</strong> be granted in the following cases:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Buyer changed their mind after purchase (buyer's remorse).</li>
              <li>Minor variations in color, texture, or appearance due to photography or screen differences.</li>
              <li>The product was accurately described as used, opened, or partially consumed, and the buyer was aware of this at the time of purchase.</li>
              <li>The buyer failed to confirm receipt within 10 days after delivery, and the order was auto-completed by the system.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg text-[#2C2A29]">4. How to Request a Refund</h2>
            <ol className="list-decimal pl-5 space-y-2">
              <li>
                <strong>Contact the seller first.</strong> We encourage buyers to reach out to the seller directly to resolve the issue. Many disputes can be
                settled amicably between both parties.
              </li>
              <li>
                <strong>Submit a refund request.</strong> If the issue cannot be resolved with the seller, contact {'{'}Beauty{'}'} Stashmart at{' '}
                <a href="mailto:hello@stashmart.store" className="text-[#D48585] underline">hello@stashmart.store</a>{' '}
                within <strong>3 days</strong> of receiving the item (or within 10 days of payment if the item was not received).
              </li>
              <li>
                <strong>Provide evidence.</strong> Include your order ID, photos or videos of the item received, and a clear explanation of the issue.
              </li>
            </ol>
          </section>

          <section>
            <h2 className="font-heading text-lg text-[#2C2A29]">5. Refund Review Process</h2>
            <p>
              Upon receiving a refund request, {'{'}Beauty{'}'} Stashmart will review the case within <strong>5 business days</strong>.
              We may contact both the buyer and the seller to gather additional information. Our decision will be based on:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>The accuracy of the original listing description.</li>
              <li>Evidence provided by the buyer (photos, videos, messages).</li>
              <li>The seller's response and cooperation.</li>
              <li>Shipping records and tracking information.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg text-[#2C2A29]">6. Refund Method</h2>
            <p>
              Approved refunds will be processed through <strong>Duitku</strong> (our payment gateway) or via bank transfer to the buyer's account.
              Refunds are typically processed within <strong>7-14 business days</strong> after approval. Shipping costs are non-refundable unless the
              refund is due to the seller's fault (e.g., item not shipped, wrong item sent).
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-[#2C2A29]">7. Return of Items</h2>
            <p>
              If a refund is approved, the buyer may be required to return the item to the seller at the buyer's expense, unless the
              refund was caused by the seller's error. The item must be returned in the same condition as received. {'{'}Beauty{'}'} Stashmart
              may waive the return requirement for low-value items or when return is impractical.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-[#2C2A29]">8. Seller Disputes</h2>
            <p>
              Sellers who disagree with a refund decision may appeal by contacting us at{' '}
              <a href="mailto:hello@stashmart.store" className="text-[#D48585] underline">hello@stashmart.store</a>{' '}
              within 7 days of the refund being issued. We will review the appeal and may reverse the refund if new evidence is provided.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-[#2C2A29]">9. Fraud Prevention</h2>
            <p>
              {'{'}Beauty{'}'} Stashmart takes refund fraud seriously. Buyers who repeatedly file unjustified refund requests or provide false evidence
              may have their accounts suspended or permanently banned. Similarly, sellers who consistently fail to deliver products as described
              may face account restrictions.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-[#2C2A29]">10. Changes to This Policy</h2>
            <p>
              We may update this Refund Policy from time to time. Changes will be posted on this page with an updated revision date.
              Continued use of the Platform after changes are posted constitutes acceptance of the revised policy.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-[#2C2A29]">11. Contact Us</h2>
            <p>
              For any refund-related inquiries, please contact us at{' '}
              <a href="mailto:hello@stashmart.store" className="text-[#D48585] underline">hello@stashmart.store</a>.
            </p>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
